/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { ShopperProducts } from '@/scapi';
import { useAnalytics } from '@/hooks/use-analytics';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';

type Product = ShopperProducts.schemas['Product'];

function getProductViewKey(productIdentity: string): string {
    const selectionParams = new URLSearchParams(window.location.search);

    // `pid` is added with a replace navigation after the default/selected variation resolves.
    // Ignoring it keeps that internal master -> variant resolution in the same logical view, while
    // the shopper-controlled variation params (color, size, etc.) distinguish real selections.
    selectionParams.delete('pid');
    selectionParams.sort();

    return `${window.location.pathname}:${productIdentity}:${selectionParams.toString()}`;
}

/** Track one product-view event per logical PDP, treating a master and its variants as one product. */
export function useProductViewTracking(product: Product): void {
    const analytics = useAnalytics();
    const selectedVariations = useSelectedVariations({ product });
    const lastTrackedProductIdRef = useRef<string | null>(null);
    const lastTrackedProductViewKeyRef = useRef<string | null>(null);
    const productIdentity = product.master?.masterId ?? product.id;
    const pendingVariantResolution = useMemo(() => {
        if (product.master?.masterId) return false;

        const matchingVariants = (product.variants ?? []).filter(({ variationValues }) =>
            Object.entries(selectedVariations).every(([attributeId, value]) => variationValues?.[attributeId] === value)
        );
        return matchingVariants.length === 1 && matchingVariants[0]?.productId !== product.id;
    }, [product.id, product.master?.masterId, product.variants, selectedVariations]);
    const analyticsRef = useRef(analytics);
    analyticsRef.current = analytics;
    const productRef = useRef(product);
    productRef.current = product;

    useEffect(() => {
        if (pendingVariantResolution || typeof window === 'undefined') return;

        const trackProductView = () => {
            const productViewKey = getProductViewKey(productIdentity);
            if (
                product.id === lastTrackedProductIdRef.current ||
                productViewKey === lastTrackedProductViewKeyRef.current
            ) {
                return;
            }

            lastTrackedProductIdRef.current = product.id;
            lastTrackedProductViewKeyRef.current = productViewKey;
            void analyticsRef.current.trackViewProduct({ product: productRef.current });
        };

        trackProductView();
    }, [pendingVariantResolution, product.id, productIdentity]);
}
